import * as React from 'react'
import { FaIcon, faXmark, faPrint } from '@/lib/fa'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { ReportType } from '@/app/constants/reportType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface PrintModalProps {
  isOpen: boolean
  onClose: () => void
  companyId: string
  selectedMonth: string // Format: "MM-YYYY"
  selectedDriverIds: string[] // Driver IDs to include in the report
  onGenerate: (selectedReports: ReportType[], options: PrintOptions) => void
}

interface PrintOptions {
  generateToSeparateFiles: boolean
  rememberSelected: boolean
}

type ReportOption = {
  type: ReportType
  labelKey: string
  label?: string
  defaultChecked: boolean
}

const REPORT_OPTIONS: ReportOption[] = [
  {
    type: ReportType.TIMESHEET_FOREIGN_AMOUNTS_REPORT, // or TIMESHEET_FOREIGN_AMOUNT_SUMMARY, TIMESHEET_FOREIGN_AMOUNTS_FOR_DRIVER_REPORT
    labelKey: 'menu.timesheet-print-foreign-amounts',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET_LUMPSUM_SETTLEMENT,
    labelKey: 'printout.timesheetLumpSumSettlement',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET_LUMPSUM_UTILIZATION,
    labelKey: 'printout.timesheetLumpSumUtilization',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET_APPLICATION_FOR_LEAVE_FOR_DRIVERS,
    labelKey: 'printout.timesheetApplicationForLeave',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET,
    labelKey: 'printout.timesheet',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET_LEAVES_CALCULATION,
    labelKey: 'printout.print-vacation-rate-calculation',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET_LEAVE_EQUIVALENT_CALCULATION,
    labelKey: 'printout.print-leave-equivalent-calculation',
    defaultChecked: false,
  },
  {
    type: ReportType.REWARDS_TO_ACCEPT_REPORT,
    labelKey: 'printout.rewardsToAcceptReport',
    defaultChecked: false,
  },
  {
    type: ReportType.MOBILITY_PACKAGE_PAYROL_REPORT,
    labelKey: 'printout.mobilityPackagePayrollReport',
    defaultChecked: false,
  },
  {
    type: ReportType.TRIP_COSTS,
    labelKey: 'printout.tripCosts',
    defaultChecked: false,
  },
  {
    type: ReportType.TIMESHEET_AMOUNTS_REPORT,
    labelKey: 'printout.timesheetAmountsReport',
    defaultChecked: false,
  },
  // { type: ReportType.TIMESHEET_DETAILED_WAGE_REPORT, labelKey: 'printout.timesheetDetailedWageReport', defaultChecked: false },
  // { type: ReportType.TIMESHEET_YEAR_SUMMARY, labelKey: 'printout.timesheetYearSummary', defaultChecked: false },
  // { type: ReportType.TIMESHEET_FOREIGN_AMOUNT_SUMMARY, labelKey: 'printout.timesheetForeignAmountSummary', defaultChecked: false },
  // { type: ReportType.TIMESHEET_LUMPSUM_SETTLEMENT_SUMMARY, labelKey: 'printout.timesheetLumpsumSettlementSummary', defaultChecked: false },
  // { type: ReportType.SELECTED_TIMESHEET_MOBILITY_PACKAGE_PAYROLL_REPORT, labelKey: 'printout.selectedMobilityPackagePayrollReport', defaultChecked: false },
  // { type: ReportType.SELECTED_REWARDS_TO_ACCEPT_REPORT, labelKey: 'printout.selectedRewardsToAcceptReport', defaultChecked: false },
]

const PrintModalComponent: React.FC<PrintModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
}) => {
  const { t } = useLocalize()
  const sortedReports = React.useMemo(() => {
    return REPORT_OPTIONS.map((o) => ({ ...o, label: t(o.labelKey), })).sort((a, b) => a.label.localeCompare(b.label))
  }, [t])
  const [selectedReports, setSelectedReports] = React.useState<Set<ReportType>>(
    () => {
      const defaultSelected = new Set<ReportType>()
      sortedReports.forEach((option) => {
        if (option.defaultChecked) {
          defaultSelected.add(option.type)
        }
      })
      return defaultSelected
    },
  )
  const [generateToSeparateFiles, setGenerateToSeparateFiles] =
    React.useState(false)
  const [rememberSelected, setRememberSelected] = React.useState(false)

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      const defaultSelected = new Set<ReportType>()
      sortedReports.forEach((option) => {
        if (option.defaultChecked) {
          defaultSelected.add(option.type)
        }
      })
      setSelectedReports(defaultSelected)
      setGenerateToSeparateFiles(false)
      setRememberSelected(false)
    }
  }, [isOpen, sortedReports])

  const handleReportToggle = (reportType: ReportType) => {
    setSelectedReports((prev) => {
      const updated = new Set(prev)
      if (updated.has(reportType)) {
        updated.delete(reportType)
      } else {
        updated.add(reportType)
      }
      return updated
    })
  }

  const handleSelectAll = () => {
    if (selectedReports.size === sortedReports.length) {
      // Deselect all
      setSelectedReports(new Set())
    } else {
      // Select all
      setSelectedReports(new Set(sortedReports.map((option) => option.type)))
    }
  }

  const handleGenerate = () => {
    if (selectedReports.size === 0) {
      return
    }
    onGenerate(Array.from(selectedReports), {
      generateToSeparateFiles,
      rememberSelected,
    })
  }

  if (!isOpen) return null

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[200] tw-flex tw-items-center tw-justify-center tw-bg-[#1f2937] tw-bg-opacity-70">
      <div className="tw-flex tw-w-[615px] tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white">
        {/* Header - 64px height */}
        <div className="tw-flex tw-h-[64px] tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-px-6">
          <h2 className="tw-text-xl tw-font-semibold tw-leading-[28px] tw-text-[#1f2937]">
            {t('printout.generate-reports')}
          </h2>
          <button
            onClick={onClose}
            className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100"
            aria-label="Close"
          >
            <FaIcon
              icon={faXmark}
              className="tw-h-5 tw-w-5 tw-text-[#1f2937]"
            />
          </button>
        </div>

        {/* Content - 288px height, scrollable */}
        <div className="tw-overflow-y-auto">
          {/* Table Header */}
          <div className="tw-sticky tw-top-0 tw-z-10 tw-grid tw-grid-cols-[40px_1fr] tw-border-b tw-border-[#e5e6e6] tw-bg-[#f2f2f2] tw-bg-opacity-25">
            <div className="tw-flex tw-h-8 tw-items-center tw-justify-center tw-p-2">
              <Checkbox
                checked={
                  selectedReports.size === sortedReports.length &&
                  sortedReports.length > 0
                }
                onChange={handleSelectAll}
                className="tw-h-4 tw-w-4"
              />
            </div>
            <div className="tw-flex tw-h-8 tw-items-center tw-gap-2 tw-px-3 tw-py-2">
              <span className="tw-text-xs tw-font-medium tw-leading-[14px] tw-text-[#1f2937]">
                {t('printout.report-name')}
              </span>
            </div>
          </div>

          {/* Report Options List */}
          <div className="tw-divide-y tw-divide-[#e5e6e6]">
            {sortedReports.map((option) => {
              const isSelected = selectedReports.has(option.type)
              const handleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
                // Don't trigger if clicking on the checkbox itself
                const target = e.target as HTMLElement
                if (
                  target.tagName === 'INPUT' ||
                  target.closest('input') ||
                  target.closest('label')
                ) {
                  return
                }
                handleReportToggle(option.type)
              }
              return (
                <div
                  key={option.type}
                  onClick={handleRowClick}
                  className="tw-grid tw-h-10 tw-cursor-pointer tw-grid-cols-[40px_1fr] tw-border-b tw-border-[#e5e6e6] hover:tw-bg-gray-50"
                >
                  <div className="tw-flex tw-items-center tw-justify-center tw-p-2">
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleReportToggle(option.type)
                      }}
                      className="tw-h-4 tw-w-4"
                    />
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2 tw-border-r tw-border-[#e5e6e6] tw-px-3 tw-py-2">
                    <FaIcon
                      icon={faPrint}
                      className="tw-h-4 tw-w-4 tw-text-[#1f2937]"
                    />
                    <span
                      className={`tw-text-xs tw-leading-[12px] tw-text-[#1f2937] ${
                        option.type === ReportType.REWARDS_TO_ACCEPT_REPORT
                          ? 'tw-font-normal'
                          : 'tw-font-medium'
                      }`}
                    >
                      {t(option.labelKey) || option.labelKey}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Divider - 1px height */}
        <div className="tw-h-px tw-bg-[#e5e6e6]"></div>

        {/* Footer - 70px height */}
        <div className="tw-flex tw-h-[70px] tw-items-center tw-border-t tw-border-[#e5e6e6] tw-bg-white tw-px-6">
          <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-6">
              {/* Generate to separate files checkbox */}
              <div className="tw-flex tw-h-4 tw-items-center tw-gap-2">
                <Checkbox
                  checked={generateToSeparateFiles}
                  onChange={(e) =>
                    setGenerateToSeparateFiles(
                      (e.target as HTMLInputElement).checked,
                    )
                  }
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-text-xs tw-font-normal tw-leading-[12px] tw-text-[#1f2937]">
                  {t('printout.generate-to-separate-files')}
                </span>
              </div>

              {/* Remember selected checkbox */}
              <div className="tw-flex tw-h-4 tw-items-center tw-gap-2">
                <Checkbox
                  checked={rememberSelected}
                  onChange={(e) =>
                    setRememberSelected((e.target as HTMLInputElement).checked)
                  }
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-text-xs tw-font-normal tw-leading-[12px] tw-text-[#1f2937]">
                  {t('printout.remember-selected')}
                </span>
              </div>
            </div>

            {/* Generate button */}
            <Button
              variant="primary"
              size="sm"
              onClick={handleGenerate}
              disabled={selectedReports.size === 0}
              className="tw-h-[38px] tw-w-[130px] tw-rounded-lg tw-text-sm tw-font-medium tw-leading-[14px]"
            >
              {t('printout.generate-reports')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const PrintModal = PrintModalComponent

export default PrintModal
